/**
 * CareStart Studio — per-variant prompt override editor (Phase 3).
 *
 * WHAT: Editable textareas for the 4 orchestrator-backed CareStart query IDs; saves to
 *   careStartVariants/{slug}.promptOverrides on explicit Save.
 * WHY: Clinic Managers customize AI reasoning per screening without mutating global
 *   `llmGlobalPrompts` (variant overrides only).
 * INVARIANT: No auto-save; voice conversation guide is omitted until a later phase (separate Firestore path).
 * Coupling: careStartVariantService.saveVariantPromptOverrides, fetchCareStartPrompts defaults.
 */

import React, { useCallback, useEffect, useId, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Chip,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { saveVariantPromptOverrides } from '../../../services/carestart/careStartVariantService';
import { CARESTART_EDITABLE_PROMPT_QUERIES } from '../constants/careStartEditablePrompts';

function buildInitialFieldValues(variant, defaultsByQueryId) {
  const saved = variant?.promptOverrides && typeof variant.promptOverrides === 'object'
    ? variant.promptOverrides
    : {};
  const values = {};
  for (const { queryId } of CARESTART_EDITABLE_PROMPT_QUERIES) {
    const savedOverride = saved[queryId];
    const defaultPrompt = defaultsByQueryId[queryId]?.prompt ?? '';
    if (typeof savedOverride === 'string' && savedOverride.trim().length > 0) {
      values[queryId] = savedOverride;
    } else {
      values[queryId] = defaultPrompt;
    }
  }
  return values;
}

function buildSavePayload(fieldValues, defaultsByQueryId) {
  const payload = {};
  for (const { queryId } of CARESTART_EDITABLE_PROMPT_QUERIES) {
    const current = String(fieldValues[queryId] ?? '').trim();
    const defaultPrompt = String(defaultsByQueryId[queryId]?.prompt ?? '').trim();
    if (current.length > 0 && current !== defaultPrompt) {
      payload[queryId] = current;
    } else {
      payload[queryId] = '';
    }
  }
  return payload;
}

export default function CareStartVariantPromptEditor({ variant, defaults, userData, onSaved }) {
  const formId = useId();
  const variantSlug = variant?.variantSlug || variant?.id || '';

  const defaultsByQueryId = useMemo(() => {
    const map = {};
    (defaults || []).forEach((row) => {
      if (row?.queryId) map[row.queryId] = row;
    });
    return map;
  }, [defaults]);

  const savedOverrides = useMemo(
    () =>
      variant?.promptOverrides && typeof variant.promptOverrides === 'object'
        ? variant.promptOverrides
        : {},
    [variant?.promptOverrides]
  );

  const [fieldValues, setFieldValues] = useState(() =>
    buildInitialFieldValues(variant, defaultsByQueryId)
  );
  const [baselineValues, setBaselineValues] = useState(() =>
    buildInitialFieldValues(variant, defaultsByQueryId)
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const next = buildInitialFieldValues(variant, defaultsByQueryId);
    setFieldValues(next);
    setBaselineValues(next);
    setError(null);
    setSuccess(false);
  }, [variant, defaultsByQueryId]);

  const isDirty = useMemo(
    () =>
      CARESTART_EDITABLE_PROMPT_QUERIES.some(
        ({ queryId }) => (fieldValues[queryId] ?? '') !== (baselineValues[queryId] ?? '')
      ),
    [fieldValues, baselineValues]
  );

  useEffect(() => {
    if (typeof window === 'undefined' || !isDirty) return undefined;
    const handler = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const isCustomized = useCallback(
    (queryId) => {
      const saved = savedOverrides[queryId];
      if (typeof saved === 'string' && saved.trim().length > 0) {
        return true;
      }
      const defaultPrompt = String(defaultsByQueryId[queryId]?.prompt ?? '').trim();
      const current = String(fieldValues[queryId] ?? '').trim();
      return current.length > 0 && current !== defaultPrompt;
    },
    [savedOverrides, fieldValues, defaultsByQueryId]
  );

  const handleRestoreDefault = useCallback(
    (queryId) => {
      const defaultPrompt = defaultsByQueryId[queryId]?.prompt ?? '';
      setFieldValues((prev) => ({ ...prev, [queryId]: defaultPrompt }));
      setSuccess(false);
    },
    [defaultsByQueryId]
  );

  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const payload = buildSavePayload(fieldValues, defaultsByQueryId);
      await saveVariantPromptOverrides(variantSlug, payload, userData);
      setBaselineValues({ ...fieldValues });
      setSuccess(true);
      onSaved?.();
    } catch (err) {
      setError(err?.message || 'Could not save prompt changes.');
    } finally {
      setSaving(false);
    }
  }, [fieldValues, defaultsByQueryId, variantSlug, userData, onSaved]);

  return (
    <Box sx={{ pt: 2, borderTop: 1, borderColor: 'divider' }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        These settings control what the AI asks and how it reasons. Changes take effect
        immediately for new conversations. If something isn&apos;t working, use &apos;Restore default&apos;
        to go back to the standard version.
      </Typography>

      <Stack spacing={2.5}>
        {CARESTART_EDITABLE_PROMPT_QUERIES.map(({ queryId, label }) => {
          const fieldLabelId = `${formId}-${queryId}-label`;
          const defaultPrompt = defaultsByQueryId[queryId]?.prompt ?? '';
          const showCustomized = isCustomized(queryId);
          return (
            <Box key={queryId}>
              <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1} sx={{ mb: 0.5 }}>
                <Typography id={fieldLabelId} variant="subtitle2" fontWeight={600}>
                  {label}
                </Typography>
                {showCustomized ? (
                  <Chip size="small" label="Customized" color="primary" variant="outlined" />
                ) : null}
                <Link
                  component="button"
                  type="button"
                  variant="caption"
                  onClick={() => handleRestoreDefault(queryId)}
                  sx={{ ml: 'auto' }}
                >
                  Restore default
                </Link>
              </Stack>
              {!defaultPrompt && !fieldValues[queryId] ? (
                <Alert severity="info" sx={{ mb: 1 }}>
                  No system default is set for this step yet. You can still save a custom prompt.
                </Alert>
              ) : null}
              <TextField
                value={fieldValues[queryId] ?? ''}
                onChange={(e) => {
                  setFieldValues((prev) => ({ ...prev, [queryId]: e.target.value }));
                  setSuccess(false);
                }}
                multiline
                fullWidth
                minRows={4}
                maxRows={20}
                variant="outlined"
                aria-labelledby={fieldLabelId}
                sx={{ '& textarea': { fontFamily: 'monospace', fontSize: 13, lineHeight: 1.5 } }}
              />
            </Box>
          );
        })}
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        Voice conversation customization is coming soon.
      </Typography>

      {error ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      ) : null}
      {success ? (
        <Alert severity="success" sx={{ mt: 2 }}>
          Prompt changes saved. They will apply to new CareStart conversations using this link.
        </Alert>
      ) : null}

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !isDirty}
        >
          {saving ? 'Saving…' : 'Save prompt changes'}
        </Button>
      </Box>
    </Box>
  );
}

CareStartVariantPromptEditor.propTypes = {
  variant: PropTypes.object.isRequired,
  defaults: PropTypes.arrayOf(PropTypes.object),
  userData: PropTypes.object,
  onSaved: PropTypes.func,
};
